
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Zap, Users, BrainCircuit, ArrowRight, LayoutDashboard, Lock } from 'lucide-react';

function LoginPage() {
  const [isLoginView, setIsLoginView] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);

  const { signUp, logIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLoginView) {
        await logIn(email, password);
        // AuthContext listener handles navigation
      } else {
        if (!displayName.trim()) throw new Error("Display Name cannot be empty.");
        await signUp(email, password, displayName);
      }
    } catch (err) {
      // Intelligent error handling
      const msg = err.message.replace('Firebase: ', '');
      if (isLoginView && (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential')) {
        toast.error("Invalid credentials. Please check your email or sign up.", { icon: '🔒' });
      } else if (err.code === 'auth/email-already-in-use') {
        toast.error("Email already in use. Please log in.");
      } else {
        toast.error(msg);
      }
      setLoading(false); 
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/');
    } catch (error) {
      toast.error('Failed to sign in with Google.');
    }
  };

  return (
    <div className="flex h-screen w-full bg-slate-50 font-sans overflow-hidden">
      
      {/* Left Side: Branding (Hidden on mobile) */}
      <div className="hidden md:flex w-1/2 bg-slate-900 relative flex-col justify-center px-16 text-white overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10 max-w-lg">
          <div className="h-16 w-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-900/50 mb-8">
             <BrainCircuit className="text-white w-10 h-10" />
          </div>
          
          <h1 className="text-5xl font-extrabold tracking-tight mb-6 leading-tight text-white">
            Master the Market with <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              AiQ Intelligence
            </span>
          </h1>
          
          <p className="text-lg text-slate-300 mb-10 leading-relaxed font-medium">
            Join thousands of individual investors leveraging institutional-grade quantitative models, AI risk management, and real-time analytics.
          </p>

          <div className="space-y-6">
            <div className="flex items-center gap-4 group">
              <div className="p-3 bg-slate-800 rounded-lg text-blue-400 group-hover:bg-blue-900/50 transition-colors"><Zap size={24} /></div>
              <div>
                <h3 className="font-bold text-white">AI-Powered Screeners</h3>
                <p className="text-sm text-slate-400">Find alpha instantly with natural language queries.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 group">
              <div className="p-3 bg-slate-800 rounded-lg text-purple-400 group-hover:bg-purple-900/50 transition-colors"><Users size={24} /></div>
              <div>
                <h3 className="font-bold text-white">Community Insights</h3>
                <p className="text-sm text-slate-400">Collaborate with top traders in real-time.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 group">
              <div className="p-3 bg-slate-800 rounded-lg text-emerald-400 group-hover:bg-emerald-900/50 transition-colors"><LayoutDashboard size={24} /></div>
              <div>
                <h3 className="font-bold text-white">Pro Terminal Access</h3>
                <p className="text-sm text-slate-400">Includes full access to the AiQ Institutional Suite.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: Auth Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white relative">
        {/* Close / Cancel button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors"
          title="Back to home"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div className="w-full max-w-md space-y-8">
          
          {/* Mobile Logo */}
          <div className="md:hidden flex justify-center mb-6">
             <div className="h-12 w-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <BrainCircuit className="text-white w-8 h-8" />
             </div>
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900">
              {isLoginView ? 'Welcome back' : 'Create an account'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {isLoginView ? 'Enter your credentials to access the terminal.' : 'Start your 14-day free trial of AiLabQuant Pro.'}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {!isLoginView && (
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
                  <input
                    id="displayName"
                    name="displayName"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all sm:text-sm bg-slate-50 focus:bg-white"
                    placeholder="John Doe"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
              )}
              
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium text-slate-700 mb-1">Email address</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all sm:text-sm bg-slate-50 focus:bg-white"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all sm:text-sm bg-slate-50 focus:bg-white"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {isLoginView && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600 cursor-pointer">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <button type="button" className="font-medium text-blue-600 hover:text-blue-500">
                    Forgot password?
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              ) : (
                <span className="flex items-center gap-2">
                   {isLoginView ? <Lock size={16} /> : <Users size={16} />}
                   {isLoginView ? 'Secure Sign In' : 'Create Account'}
                </span>
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                onClick={handleGoogleSignIn}
                className="w-full inline-flex justify-center py-2.5 px-4 border border-slate-300 rounded-lg shadow-sm bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                   <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                   <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                   <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                   <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Google
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm mt-6">
            <span className="text-slate-500">
              {isLoginView ? "Don't have an account?" : "Already have an account?"}
            </span>
            <button 
              onClick={() => { setIsLoginView(!isLoginView); }}
              className="font-bold text-blue-600 hover:text-blue-500 flex items-center gap-1 hover:gap-2 transition-all"
            >
              {isLoginView ? 'Sign up now' : 'Log in'} <ArrowRight size={14} />
            </button>
          </div>

        </div>
        
        <div className="absolute bottom-6 text-xs text-slate-400">
          © 2024 AiLabQuant. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
    